<template>
  <!-- A static row scored on a bid the campaign no longer has, i.e. between an inline bid edit
       and the refetch that follows it. Anything below would be computed from the old bid -->
  <div
    v-if="isPositionStale"
    v-b-tooltip.hover
    class="text-muted"
    :title="stalePositionTooltip"
    data-cy="text-position-stale"
  >
    -
  </div>

  <!-- Nothing to raise here - show the campaign's standing instead. That is a leading
       static campaign, or any dynamic campaign, which never gets a raise chip -->
  <div
    v-else-if="showsRank"
    v-b-tooltip.hover
    class="text-nowrap"
    :title="rankBandTooltip"
    data-cy="opportunity-rank"
  >
    <span :class="rankTextClass">{{ getRankBandText(marketPosition.rankBand) }}</span>
    <span class="text-muted">{{ windowSuffix }}</span>
  </div>

  <b-button
    v-else-if="hasSuggestion"
    variant="outline-primary"
    size="sm"
    class="text-nowrap"
    data-cy="button-apply-suggested-bid"
    @click="$emit('apply', {
      campaign,
      suggestedBidInUSD: marketPosition.suggestedBidInUSD,
      estimatedAdditionalCallsPerDay: marketPosition.estimatedAdditionalCallsPerDay
    })"
  >
    +{{ formatCurrency(raiseAmountInUSD) }}
    <span v-if="marketPosition.estimatedAdditionalCallsPerDay">
      → est. +{{ marketPosition.estimatedAdditionalCallsPerDay }} calls/day
    </span>
    <span v-else>
      → enters market range
    </span>
  </b-button>

  <!-- Already at or above the suggestion - the state a campaign lands in right after acting
       on a raise, which would otherwise read as a blank cell -->
  <div
    v-else-if="isAtSuggestedBid"
    v-b-tooltip.hover
    class="text-muted text-nowrap"
    :title="atSuggestedBidTooltip"
    data-cy="text-at-suggested-bid"
  >
    At suggested bid
  </div>

  <div
    v-else
    class="text-muted"
  >
    -
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import { bidTiers, getRankBandText, getRankBandTooltip, toCents } from '@/store/modules/market-context.store'

export default {
  name: 'BidOpportunityCell',

  props: {
    campaign: {
      type: Object,
      required: true
    },

    // Market position row for this campaign, see MarketContextAPI.getCampaignMarketPositions
    marketPosition: {
      type: Object,
      default: null
    },

    // Static campaigns are the only ones that can act on a raise suggestion:
    // campaign/updateCampaignBid writes maxBidPerCallInUSD, which an RTB or Programmatic
    // campaign ignores because its bidder prices every ping itself
    isStatic: {
      type: Boolean,
      default: true
    },

    // The window the rank band was measured over, as the `short` form of formatMarketWindow
    // ('24h' or '7d'). The band still moves with the window: the market cell counts any buyer
    // with a single bid in the window, so a 7-day window picks up every occasional bidder and
    // can demote a campaign a band from where it sat on a 24h window, with nobody having
    // changed anything. Unqualified, "Top 25% of bids" on Tuesday and "Lower 50% of bids"
    // on Monday reads as a real regression instead of a wider lookback
    windowShort: {
      type: String,
      default: ''
    }
  },

  computed: {
    // The endpoint scored a static row on the campaign's configured bid AT FETCH TIME. An inline
    // edit puts the new bid in the store at once, but the row keeps the old one until the refetch
    // lands, and a chip computed from it could offer to "raise" a $30 bid to a $15 suggestion.
    // Only a static campaign can be told stale this way: a dynamic row is positioned on its
    // observed median, which no config change moves.
    isPositionStale() {
      return this.isStatic
        && this.marketPosition?.positionedBidInUSD != null
        && this.campaign?.budget?.maxBidPerCallInUSD != null
        && toCents(this.marketPosition.positionedBidInUSD) !== toCents(this.campaign.budget.maxBidPerCallInUSD)
    },

    stalePositionTooltip() {
      return 'The bid changed after this campaign\'s market position was measured. Its suggestion returns once the position refreshes.'
    },

    windowSuffix() {
      return this.windowShort ? ` (${this.windowShort})` : ''
    },

    // A leading static campaign has nothing to raise; a dynamic campaign has no raise it
    // could act on at all, so it shows its standing whenever the endpoint ranked it
    showsRank() {
      return !this.isStatic
        ? this.marketPosition?.rankBand != null
        : (this.marketPosition?.tier === bidTiers.Leading && this.marketPosition?.rankBand != null)
    },

    // Names the comparison the band makes - which matters because the tier badge one column
    // over makes a DIFFERENT one and the two can disagree in the same row. The band is scored
    // on the ungated bidder list and the badge on delivered auctions only, so the badge's
    // claim rests on strictly fewer data points than the band's; the "of bids" wording in
    // getRankBandText - ALL bids, not winning ones - is what keeps that legible and must
    // stay. The tooltip stops short of saying so; the pair of column
    // headers is what names the two populations apart. See getRankBandTooltip in the store,
    // docs/MARKET_CONTEXT_DELIVERED_CALLS.md for the population, and
    // docs/MARKET_CONTEXT_WIRING.md for the open decision on whether labelling it at all is
    // enough
    rankBandTooltip() {
      return getRankBandTooltip()
    },

    rankTextClass() {
      // Green reads as "you are winning", which only holds for the leading static case
      return this.isStatic ? 'text-success' : 'text-muted'
    },

    // The bid the row was positioned on, so the raise is measured against the same number
    // the suggestion was derived from; falls back to the campaign config only if the
    // endpoint's row is absent
    currentBidInUSD() {
      return this.marketPosition?.positionedBidInUSD ?? this.campaign?.budget?.maxBidPerCallInUSD
    },

    raiseAmountInUSD() {
      return this.marketPosition.suggestedBidInUSD - this.currentBidInUSD
    },

    hasSuggestion() {
      return this.isStatic
        && this.marketPosition?.suggestedBidInUSD != null
        && this.currentBidInUSD != null
        && this.raiseAmountInUSD > 0
    },

    // The state a Static campaign lands in the moment it acts on a raise: the endpoint still
    // suggests a bid, but the campaign is now at or above it, so there is no chip left and no
    // Leading tier to report. Without this the cell falls to a bare dash and the buyer reads
    // their own successful raise as missing data
    isAtSuggestedBid() {
      return this.isStatic
        && this.marketPosition?.suggestedBidInUSD != null
        && this.currentBidInUSD != null
        && this.raiseAmountInUSD <= 0
    },

    // Names the window only once it is known, for the same reason noBidsText in
    // MarketPositionIndicator does
    atSuggestedBidTooltip() {
      const windowPhrase = this.windowShort ? ` ${this.windowShort}` : ''

      return `Your bid is at or above the suggested bid. Call estimates only move as new auctions accumulate over the${windowPhrase} window`
    }
  },

  methods: {
    formatCurrency,
    getRankBandText
  }
}
</script>

<style lang="scss" scoped></style>
